import { useState, useEffect, useCallback, useRef } from "react";
import { voiceApi } from "../services/api";

export interface CallState {
  status: "idle" | "connecting" | "ringing" | "open" | "closed" | "error";
  direction: "inbound" | "outbound" | null;
  remoteNumber: string | null;
  remoteName: string | null;
  startedAt: Date | null;
  duration: number;
  isMuted: boolean;
  callSid: string | null;
  callLogId: string | null;
  mock: boolean;
}

const initialCallState: CallState = {
  status: "idle",
  direction: null,
  remoteNumber: null,
  remoteName: null,
  startedAt: null,
  duration: 0,
  isMuted: false,
  callSid: null,
  callLogId: null,
  mock: false,
};

export interface IncomingCallInfo {
  callSid: string;
  from: string;
  to: string;
  call: any;
}

export function useTwilioDevice() {
  const [deviceReady, setDeviceReady] = useState(false);
  const [callState, setCallState] = useState<CallState>(initialCallState);
  const [incomingCall, setIncomingCall] = useState<IncomingCallInfo | null>(
    null
  );
  const deviceRef = useRef<any>(null);
  const activeCallRef = useRef<any>(null);
  const durationIntervalRef = useRef<ReturnType<typeof setInterval> | null>(
    null
  );
  const mockMode = useRef(false);
  const mockTimeoutsRef = useRef<ReturnType<typeof setTimeout>[]>([]);

  // Start duration timer
  const startDurationTimer = useCallback(() => {
    if (durationIntervalRef.current) clearInterval(durationIntervalRef.current);
    durationIntervalRef.current = setInterval(() => {
      setCallState((prev) => ({
        ...prev,
        duration: prev.startedAt
          ? Math.floor((Date.now() - prev.startedAt.getTime()) / 1000)
          : 0,
      }));
    }, 1000);
  }, []);

  const stopDurationTimer = useCallback(() => {
    if (durationIntervalRef.current) {
      clearInterval(durationIntervalRef.current);
      durationIntervalRef.current = null;
    }
  }, []);

  const resetCallState = useCallback(() => {
    stopDurationTimer();
    setCallState(initialCallState);
    setIncomingCall(null);
    activeCallRef.current = null;
  }, [stopDurationTimer]);

  // Refresh token helper (shared by interval and tokenExpired handler)
  const refreshToken = useCallback(async () => {
    if (mockMode.current || !deviceRef.current) return;
    try {
      const res = await voiceApi.getToken();
      deviceRef.current.updateToken(res.data.token);
    } catch (err) {
      console.error("[TwilioDevice] Token refresh failed:", err);
    }
  }, []);

  // Fire-and-forget helper to notify backend that a call ended.
  // Does NOT pass status — the backend determines answered/missed from
  // Twilio's status webhooks (which set answeredAt on the CallLog).
  const fireEndCall = useCallback((logId: string | null, startedAt: Date | null) => {
    if (!logId) return;
    const durationSecs = startedAt
      ? Math.floor((Date.now() - startedAt.getTime()) / 1000)
      : 0;
    voiceApi.endCall(logId, { durationSecs }).catch((err: any) => {
      console.error("[TwilioDevice] endCall failed:", err);
    });
  }, []);

  // Initialize device  -  deferred until first user interaction to avoid AudioContext warning
  useEffect(() => {
    let destroyed = false;
    let initDone = false;

    const init = async () => {
      if (initDone || destroyed) return;
      initDone = true;

      try {
        const res = await voiceApi.getToken();
        const { token, mock } = res.data;

        if (destroyed) return;

        if (mock) {
          mockMode.current = true;
          setDeviceReady(true);
          return;
        }

        // Dynamic import of Twilio Voice SDK
        const { Device } = await import("@twilio/voice-sdk");
        const device = new Device(token);

        device.on("registered", () => {
          if (!destroyed) setDeviceReady(true);
        });

        device.on("error", (err: any) => {
          console.error("[TwilioDevice] Error:", err);
          // Token already expired (20104)  -  refresh immediately and re-register
          if (err?.code === 20104) {
            refreshToken().then(() => {
              deviceRef.current?.register();
            });
          }
        });

        // Handle token expiry  -  immediately refresh instead of waiting for interval
        device.on("tokenWillExpire", () => {
          refreshToken();
        });

        device.on("incoming", (call: any) => {
          if (destroyed) return;
          const info: IncomingCallInfo = {
            callSid: call.parameters?.CallSid || "",
            from: call.parameters?.From || "",
            to: call.parameters?.To || "",
            call,
          };
          setIncomingCall(info);
          setCallState((prev) => ({
            ...prev,
            status: "ringing",
            direction: "inbound",
            remoteNumber: info.from,
            callSid: info.callSid,
            mock: false,
          }));

          call.on("accept", () => {
            activeCallRef.current = call;
            const now = new Date();
            setCallState((prev) => ({
              ...prev,
              status: "open",
              startedAt: now,
            }));
            startDurationTimer();
          });

          call.on("disconnect", () => {
            stopDurationTimer();
            // Notify backend before resetting state
            setCallState((prev) => {
              fireEndCall(prev.callLogId, prev.startedAt);
              return { ...prev, status: "closed" };
            });
            setTimeout(resetCallState, 3000);
          });

          call.on("cancel", () => {
            resetCallState();
          });
        });

        device.register();
        deviceRef.current = device;
      } catch (err) {
        console.error("[TwilioDevice] Init failed:", err);
      }
    };

    // Defer initialization until a user gesture (click/keydown) to satisfy
    // Chrome's AudioContext autoplay policy.
    const onUserGesture = () => {
      init();
      document.removeEventListener("click", onUserGesture);
      document.removeEventListener("keydown", onUserGesture);
    };

    document.addEventListener("click", onUserGesture);
    document.addEventListener("keydown", onUserGesture);

    return () => {
      destroyed = true;
      document.removeEventListener("click", onUserGesture);
      document.removeEventListener("keydown", onUserGesture);
      deviceRef.current?.destroy();
      stopDurationTimer();
      mockTimeoutsRef.current.forEach(clearTimeout);
    };
  }, [startDurationTimer, stopDurationTimer, resetCallState, refreshToken, fireEndCall]);

  // Token refresh every 50 minutes as a safety net
  useEffect(() => {
    const interval = setInterval(
      async () => {
        refreshToken();
      },
      50 * 60 * 1000
    );
    return () => clearInterval(interval);
  }, [refreshToken]);

  // Make outbound call
  const makeCall = useCallback(
    async (to: string, from: string, conversationId: string) => {
      // Create call log on server
      let callLogId: string | null = null;
      try {
        const res = await voiceApi.initiateCall({ to, from, conversationId });
        callLogId = res.data?.callLogId || res.data?._id || null;
      } catch (err) {
        console.error("[TwilioDevice] Failed to create call log:", err);
      }

      if (mockMode.current) {
        // Simulate call state transitions
        setCallState({
          ...initialCallState,
          status: "connecting",
          direction: "outbound",
          remoteNumber: to,
          callLogId,
          mock: true,
        });

        const t1 = setTimeout(() => {
          setCallState((prev) => ({ ...prev, status: "ringing" }));
        }, 1000);

        const t2 = setTimeout(() => {
          const now = new Date();
          setCallState((prev) => ({
            ...prev,
            status: "open",
            startedAt: now,
          }));
          startDurationTimer();
        }, 3000);

        mockTimeoutsRef.current = [t1, t2];
        return;
      }

      if (!deviceRef.current) return;

      setCallState({
        ...initialCallState,
        status: "connecting",
        direction: "outbound",
        remoteNumber: to,
        callLogId,
        mock: false,
      });

      try {
        const call = await deviceRef.current.connect({
          params: { To: to, From: from, ConversationId: conversationId },
        });
        activeCallRef.current = call;

        call.on("ringing", () => {
          setCallState((prev) => ({ ...prev, status: "ringing" }));
        });

        call.on("accept", () => {
          const now = new Date();
          setCallState((prev) => ({
            ...prev,
            status: "open",
            startedAt: now,
          }));
          startDurationTimer();
        });

        call.on("disconnect", () => {
          stopDurationTimer();
          // Notify backend before resetting state
          setCallState((prev) => {
            fireEndCall(prev.callLogId, prev.startedAt);
            return { ...prev, status: "closed" };
          });
          setTimeout(resetCallState, 3000);
        });

        call.on("error", (err: any) => {
          console.error("[TwilioDevice] Call error:", err);
          setCallState((prev) => ({ ...prev, status: "error" }));
          setTimeout(resetCallState, 3000);
        });
      } catch (err) {
        console.error("[TwilioDevice] Connect failed:", err);
        setCallState((prev) => ({ ...prev, status: "error" }));
        setTimeout(resetCallState, 3000);
      }
    },
    [startDurationTimer, stopDurationTimer, resetCallState, fireEndCall]
  );

  const acceptCall = useCallback(() => {
    incomingCall?.call?.accept();
  }, [incomingCall]);

  const rejectCall = useCallback(() => {
    incomingCall?.call?.reject();
    resetCallState();
  }, [incomingCall, resetCallState]);

  const hangup = useCallback(() => {
    if (mockMode.current) {
      mockTimeoutsRef.current.forEach(clearTimeout);
      stopDurationTimer();
      // Notify backend before resetting state
      setCallState((prev) => {
        fireEndCall(prev.callLogId, prev.startedAt);
        return { ...prev, status: "closed" };
      });
      setTimeout(resetCallState, 2000);
      return;
    }
    activeCallRef.current?.disconnect();
  }, [stopDurationTimer, resetCallState, fireEndCall]);

  const toggleMute = useCallback(() => {
    if (mockMode.current) {
      setCallState((prev) => ({ ...prev, isMuted: !prev.isMuted }));
      return;
    }
    const call = activeCallRef.current;
    if (call) {
      const muted = !call.isMuted();
      call.mute(muted);
      setCallState((prev) => ({ ...prev, isMuted: muted }));
    }
  }, []);

  return {
    deviceReady,
    callState,
    incomingCall,
    makeCall,
    acceptCall,
    rejectCall,
    hangup,
    toggleMute,
  };
}
